// ── Wayback Machine 首次快照查询 ──────────────────────────
// 使用 CDX API 获取域名最早一条 archive 记录
// 浏览器端通过 Railway 代理请求，避免 CORS 限制

async function cdxEarliest(url) {
  if (!url) return null;
  try {
    // 走 Railway 代理，避免 Wayback CDX API 无 CORS 头导致浏览器拦截
    const proxyUrl = `/wb-snap?q=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data) || data.length < 2) return null;
    const ts = data[1][0];
    if (!ts || ts.length < 8) return null;
    const isoDate = `${ts.slice(0, 4)}-${ts.slice(4, 6)}-${ts.slice(6, 8)}`;
    return {
      date: isoDate,
      url: `https://web.archive.org/web/${ts}/${data[1][1] || url}`,
      origUrl: url,  // 原始 URL，用于判断来源类型
    };
  } catch { return null; }
}

// 从 URL 推断文档托管来源（用于「文档来源」徽章）
export function inferDocSource(url) {
  if (!url) return 'unknown';
  const u = url.toLowerCase();
  if (u.includes('gitbook.io')) return 'gitbook';
  if (u.includes('mintlify.app')) return 'mintlify';
  if (u.includes('github.io')) return 'github-pages';
  if (u.includes('github.com')) return 'github-readme';
  if (u.includes('notion.so') || u.includes('notion.site')) return 'notion';
  if (u.includes('readme.io')) return 'readme-io';
  if (u.includes('docusaurus')) return 'docusaurus';
  if (/\/docs\b/.test(u)) return 'docs-path';
  if (/^https?:\/\/docs\./i.test(u) || u.startsWith('docs.')) return 'docs-subdomain';
  if (u.includes('whitepaper')) return 'whitepaper';
  if (u.includes('learn.')) return 'learn-subdomain';
  return 'website';
}

function extractDomain(websiteUrl) {
  try {
    const u = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return null; }
}

/**
 * 查官网最早快照
 */
export async function fetchFirstSnapshot(websiteUrl) {
  if (!websiteUrl) return null;
  const domain = extractDomain(websiteUrl);
  if (!domain) return null;
  const result = await cdxEarliest(domain);
  if (!result) return null;
  return { firstSnapshotDate: result.date, firstSnapshotUrl: result.url };
}

/**
 * 查白皮书/文档的最早快照
 * 扩展尝试 15+ URL 模式，覆盖主流 Web3 文档托管平台
 */
export async function fetchFirstDocSnapshot(socialMedia, projectName = '') {
  if (!socialMedia && !projectName) return null;
  socialMedia = socialMedia || {};

  const docsUrl  = socialMedia.gitbook || socialMedia.docs || socialMedia.whitepaper || '';
  const website  = socialMedia.website || '';
  const domain   = extractDomain(website);
  // 项目名归一化为 slug（"Forecast Protocol" → "forecast" / "forecastprotocol"）
  const slug = (projectName || '').toLowerCase().trim().replace(/\s+/g, '');
  const firstWord = (projectName || '').toLowerCase().split(/\s+/)[0];

  const candidates = [];
  // 1. RootData 提供的 URL（最准）
  if (docsUrl) candidates.push(docsUrl);

  // 2. 基于官网域名的常见模式
  if (domain) {
    candidates.push(
      `docs.${domain}`,
      `${domain}/docs`,
      `${domain}/whitepaper`,
      `${domain}/litepaper`,
      `${domain}/learn`,
      `${domain}/documentation`,
      `learn.${domain}`,
      `whitepaper.${domain}`,
    );
  }

  // 3. 基于项目名的 GitBook / 托管平台候选
  if (slug && slug.length >= 3) {
    candidates.push(
      `${slug}.gitbook.io`,
      `gitbook.io/${slug}`,
      `${slug}.mintlify.app`,
      `${slug}.github.io`,
      `docs.${slug}.io`,
      `docs.${slug}.xyz`,
      `docs.${slug}.network`,
      `docs.${slug}.finance`,
    );
  }
  if (firstWord && firstWord !== slug && firstWord.length >= 3) {
    candidates.push(
      `${firstWord}.gitbook.io`,
      `docs.${firstWord}.xyz`,
      `docs.${firstWord}.io`,
    );
  }

  // 去重 + 限制数量（避免 Wayback 过载）
  const unique = [...new Set(candidates.filter(Boolean))].slice(0, 15);

  let bestWayback = null;
  if (unique.length > 0) {
    // 分批并行查询（每批 4 个），找到结果即提前退出，避免 20 并发压垮 Railway。
    // 整体 8s 截止 — 否则 15 candidates × 6s/batch = 最长 24s（Sui 用例实测）。
    const deadline = Date.now() + 8000;
    const allValid = [];
    for (let i = 0; i < unique.length; i += 4) {
      if (Date.now() >= deadline) break;
      const batch = unique.slice(i, i + 4);
      const results = await Promise.all(batch.map(u => cdxEarliest(u)));
      const valid = results.filter(Boolean);
      allValid.push(...valid);
      if (allValid.length > 0) break;
    }
    if (allValid.length > 0) {
      allValid.sort((a, b) => a.date.localeCompare(b.date));
      bestWayback = allValid[0];
    }
  }

  // ── GitHub README 兜底 ─────────────────────────────────
  // 当 Wayback 全部失败时，用 README.md 首次 commit 作为「首次文档公开」
  // 因为 README 在大多数 Web3 项目里就是最早的对外文档
  const githubUrl = socialMedia.github || '';
  if (!bestWayback && githubUrl) {
    try {
      const m = githubUrl.match(/github\.com\/([^\/\s]+)\/([^\/\s\?#]+)/);
      if (m) {
        const owner = m[1];
        const repo = m[2].replace(/\.git$/, '');
        const { fetchGitHubReadmeFirstCommit } = await import('./github');
        const readme = await fetchGitHubReadmeFirstCommit(owner, repo).catch(() => null);
        if (readme) {
          return {
            firstDocDate: readme.date,
            firstDocUrl: readme.url,
            sourceType: 'github-readme',
            sourceLabel: 'GitHub README',
          };
        }
      }
    } catch { /* 静默 */ }
  }

  if (!bestWayback) return null;

  const sourceType = inferDocSource(bestWayback.origUrl);
  return {
    firstDocDate: bestWayback.date,
    firstDocUrl:  bestWayback.url,
    sourceType,
    sourceLabel: sourceType === 'gitbook' ? 'GitBook'
              : sourceType === 'mintlify' ? 'Mintlify'
              : sourceType === 'github-pages' ? 'GitHub Pages'
              : sourceType === 'docs-subdomain' ? 'docs.' + extractDomain(bestWayback.origUrl)?.replace(/^docs\./, '') || 'docs.'
              : sourceType === 'docs-path' ? 'Wayback /docs'
              : sourceType === 'whitepaper' ? 'Whitepaper'
              : sourceType === 'learn-subdomain' ? 'Learn'
              : 'Wayback',
  };
}
